import type { Language } from '../types/lead'

/**
 * Flat key-based translation dictionary.
 * Never duplicate components per language and never inline `lang === 'es' ? ... : ...` in JSX.
 */
export const translations: Record<string, Record<Language, string>> = {
  // Header
  'nav.brand': { en: 'MIAMI CAR QUEENS', es: 'MIAMI CAR QUEENS' },
  'nav.cta': { en: 'CHECK MY OPTIONS', es: 'VER MIS OPCIONES' },
  'nav.language': { en: 'Language', es: 'Idioma' },
  'nav.skip': { en: 'Skip to main content', es: 'Ir al contenido principal' },

  // Hero
  'hero.eyebrow': {
    en: 'MIAMI CAR QUEENS · GABRIELA + CHANIRA · MIAMI / DORAL, FL',
    es: 'MIAMI CAR QUEENS · GABRIELA + CHANIRA · MIAMI / DORAL, FL',
  },
  'hero.headline1': { en: 'YOUR NEXT CAR.', es: 'TU PRÓXIMO CARRO.' },
  'hero.headline2': { en: 'YOUR BEST DEAL.', es: 'TU MEJOR DEAL.' },
  'hero.sub': {
    en: 'Personal attention, options for different situations, and zero pressure. We help you find the right vehicle and understand your options.',
    es: 'Atención personal, opciones para diferentes situaciones y cero presión. Te ayudamos a encontrar el carro correcto y entender tus opciones.',
  },
  'hero.ctaPrimary': { en: 'CHECK MY OPTIONS', es: 'VER MIS OPCIONES' },
  'hero.ctaSecondary': { en: 'TALK TO THE QUEENS', es: 'HABLAR CON LAS QUEENS' },
  'hero.chip1': { en: 'New & Used', es: 'Nuevos y Usados' },
  'hero.chip2': { en: 'Leasing & Financing', es: 'Leasing y Financiamiento' },
  'hero.chip3': { en: 'Trade-Ins Welcome', es: 'Aceptamos Trade-Ins' },
  'hero.chip4': { en: 'Español & English', es: 'Español & English' },
  'hero.tagline': { en: 'NO PRESSURE. JUST THE BEST DEAL.', es: 'NO PRESSURE. JUST THE BEST DEAL.' },
  'hero.photoAlt': {
    en: 'Gabriela and Chanira, the Miami Car Queens',
    es: 'Gabriela y Chanira, las Miami Car Queens',
  },
  'hero.photo': {
    en: '[REPLACE WITH APPROVED HERO PHOTO — GABRIELA + CHANIRA, HORIZONTAL, NATURAL DAYLIGHT]',
    es: '[REEMPLAZAR CON FOTO HERO APROBADA — GABRIELA + CHANIRA, HORIZONTAL, LUZ NATURAL]',
  },

  // Trust bar
  'trust.1.title': { en: 'VIP SERVICE', es: 'SERVICIO VIP' },
  'trust.1.desc': {
    en: 'Personal attention from the first message to the next step.',
    es: 'Atención personal desde el primer mensaje hasta el siguiente paso.',
  },
  'trust.2.title': { en: 'FINANCING OPTIONS', es: 'OPCIONES DE FINANCIAMIENTO' },
  'trust.2.desc': {
    en: 'Explore available options based on your situation.',
    es: 'Explora las opciones disponibles según tu situación.',
  },
  'trust.3.title': { en: 'NEW · USED · LEASING', es: 'NUEVOS · USADOS · LEASING' },
  'trust.3.desc': {
    en: 'More than one way to find your next vehicle.',
    es: 'Más de una forma de encontrar tu próximo vehículo.',
  },

  // Meet the Queens
  'queens.eyebrow': { en: 'MEET YOUR CAR QUEENS', es: 'CONOCE A TUS CAR QUEENS' },
  'queens.title': { en: 'TWO WOMEN. ONE MISSION.', es: 'DOS MUJERES. UNA MISMA MISIÓN.' },
  'queens.support': {
    en: 'We love selling cars, but the relationships we build with our customers are what make the experience different.',
    es: 'Nos encanta vender carros, pero las relaciones que construimos con nuestros clientes son lo que hace diferente la experiencia.',
  },
  'queens.gabriela.bio': {
    en: 'Direct attention, clear communication, and a customer-first approach designed to help you understand your options before making a decision.',
    es: 'Atención directa, comunicación clara y un enfoque centrado en el cliente para ayudarte a entender tus opciones antes de decidir.',
  },
  'queens.chanira.bio': {
    en: 'Focused on a personal, comfortable experience from the first conversation through the entire process.',
    es: 'Enfocada en una experiencia personal y cómoda desde la primera conversación y durante todo el proceso.',
  },
  'queens.quote': {
    en: '“More than selling cars, we want people to feel confident about the decision they’re making.”',
    es: '“Más que vender carros, queremos que las personas se sientan seguras de la decisión que están tomando.”',
  },
  'queens.alt.gabriela': {
    en: 'Gabriela, automotive sales professional at Miami Car Queens',
    es: 'Gabriela, profesional de ventas automotrices de Miami Car Queens',
  },
  'queens.alt.chanira': {
    en: 'Chanira, automotive sales professional at Miami Car Queens',
    es: 'Chanira, profesional de ventas automotrices de Miami Car Queens',
  },
  'queens.alt.both': {
    en: 'Gabriela, on the left, and Chanira, on the right — the Miami Car Queens',
    es: 'Gabriela, a la izquierda, y Chanira, a la derecha — las Miami Car Queens',
  },
  'queens.caption': {
    en: 'Gabriela · Chanira',
    es: 'Gabriela · Chanira',
  },
  'queens.photo.gabriela': {
    en: '[REPLACE WITH APPROVED PORTRAIT — GABRIELA]',
    es: '[REEMPLAZAR CON RETRATO APROBADO — GABRIELA]',
  },
  'queens.photo.chanira': {
    en: '[REPLACE WITH APPROVED PORTRAIT — CHANIRA]',
    es: '[REEMPLAZAR CON RETRATO APROBADO — CHANIRA]',
  },
  'queens.photo.wide': {
    en: '[REPLACE WITH APPROVED GABRIELA + CHANIRA PHOTO]',
    es: '[REEMPLAZAR CON FOTO APROBADA GABRIELA + CHANIRA]',
  },

  // Queen experience
  'exp.eyebrow': { en: 'THE QUEEN EXPERIENCE', es: 'LA EXPERIENCIA QUEEN' },
  'exp.title': { en: 'TWO WAYS TO BUY A CAR.', es: 'DOS FORMAS DE COMPRAR UN CARRO.' },
  'exp.typicalTitle': { en: 'THE TYPICAL EXPERIENCE', es: 'LA EXPERIENCIA TÍPICA' },
  'exp.typical.1': { en: 'Too much pressure', es: 'Demasiada presión' },
  'exp.typical.2': { en: 'Confusing information', es: 'Información confusa' },
  'exp.typical.3': { en: 'Feeling like just another sale', es: 'Sentirte como una venta más' },
  'exp.typical.4': { en: 'Not knowing who to trust', es: 'No saber en quién confiar' },
  'exp.typical.5': {
    en: 'Getting passed from person to person',
    es: 'Que te pasen de persona en persona',
  },
  'exp.badge': { en: 'THE QUEEN WAY', es: 'THE QUEEN WAY' },
  'exp.queenTitle': { en: 'THE QUEEN EXPERIENCE', es: 'LA EXPERIENCIA QUEEN' },
  'exp.queen.1': { en: 'We listen first', es: 'Primero te escuchamos' },
  'exp.queen.2': { en: 'We explain your options clearly', es: 'Te explicamos tus opciones con claridad' },
  'exp.queen.3': { en: 'We help you compare', es: 'Te ayudamos a comparar' },
  'exp.queen.4': { en: 'Personal attention', es: 'Atención personal' },
  'exp.queen.5': {
    en: 'Support from conversation to delivery',
    es: 'Acompañamiento desde la conversación hasta la entrega',
  },
  'exp.cta': { en: 'LIVE THE DIFFERENCE →', es: 'VIVE LA DIFERENCIA →' },

  // Trade-in capture
  'tradein.title': { en: 'ALREADY HAVE A CAR?', es: '¿YA TIENES UN CARRO?' },
  'tradein.sub': {
    en: 'It may be worth more than you think. Tell us what you drive and we’ll look into it.',
    es: 'Puede valer más de lo que crees. Cuéntanos qué manejas y lo revisamos contigo.',
  },
  'tradein.year': { en: 'Year', es: 'Año' },
  'tradein.make': { en: 'Make', es: 'Marca' },
  'tradein.model': { en: 'Model', es: 'Modelo' },
  'tradein.cta': { en: 'REVIEW MY TRADE-IN', es: 'REVISAR MI TRADE-IN' },
  'tradein.note': {
    en: 'We review it with you personally. No obligation.',
    es: 'Lo revisamos contigo personalmente. Sin compromiso.',
  },
  'tradein.err': {
    en: 'Please complete year, make, and model.',
    es: 'Por favor completa año, marca y modelo.',
  },

  // Kia warranty (verified manufacturer information)
  'warranty.title': { en: 'KIA FACTORY WARRANTY', es: 'GARANTÍA DE FÁBRICA KIA' },
  'warranty.text': {
    en: 'New Kia vehicles include a 10-year / 100,000-mile powertrain warranty and a 5-year / 60,000-mile basic warranty.',
    es: 'Los vehículos Kia nuevos incluyen garantía de fábrica de 10 años / 100,000 millas en tren motriz y 5 años / 60,000 millas básica.',
  },
  'warranty.small': {
    en: 'All warranties and roadside assistance are limited. Confirm the details with the dealership.',
    es: 'Todas las garantías y la asistencia en carretera son limitadas. Consulta los detalles con el concesionario.',
  },

  // Lead form
  'form.title': { en: 'LET’S FIND YOUR BEST OPTION', es: 'VAMOS A ENCONTRAR TU MEJOR OPCIÓN' },
  'form.sub': {
    en: 'Takes less than 60 seconds. No obligation.',
    es: 'Toma menos de 60 segundos. Sin compromiso.',
  },
  'form.firstName': { en: 'First name', es: 'Nombre' },
  'form.phone': { en: 'Phone', es: 'Teléfono' },
  'form.email': { en: 'Email', es: 'Email' },
  'form.optional': { en: 'optional', es: 'opcional' },
  'form.vehicle': { en: 'What are you looking for?', es: '¿Qué estás buscando?' },
  'form.intent': { en: 'What would you like to do?', es: '¿Qué te gustaría hacer?' },
  'form.credit': { en: 'How would you describe your credit?', es: '¿Cómo describirías tu crédito?' },
  'form.choose': { en: 'Select an option', es: 'Selecciona una opción' },
  'form.opt.suv': { en: 'SUV', es: 'SUV' },
  'form.opt.sedan': { en: 'Sedan', es: 'Sedán' },
  'form.opt.family': { en: 'Family', es: 'Familiar' },
  'form.opt.hybrid-ev': { en: 'Hybrid / EV', es: 'Híbrido / EV' },
  'form.opt.not-sure': { en: 'Not sure', es: 'No estoy segura/o' },
  'form.opt.buy': { en: 'Buy', es: 'Comprar' },
  'form.opt.lease': { en: 'Lease', es: 'Leasing' },
  'form.opt.trade-in-buy': { en: 'Trade-In + Buy', es: 'Trade-In + Comprar' },
  'form.opt.excellent': { en: 'Excellent', es: 'Excelente' },
  'form.opt.good': { en: 'Good', es: 'Bueno' },
  'form.opt.fair': { en: 'Fair', es: 'Regular' },
  'form.opt.rebuilding': { en: 'Rebuilding', es: 'En reconstrucción' },
  'form.opt.first-time': { en: 'First-Time Buyer', es: 'Primera compra' },
  'form.consent': {
    en: 'I agree that Miami Car Queens may contact me by WhatsApp, SMS, phone, or email about my request. I can opt out anytime by replying STOP. Standard message rates may apply.',
    es: 'Acepto que Miami Car Queens me contacte por WhatsApp, SMS, llamada o email sobre mi solicitud. Puedo cancelar en cualquier momento respondiendo STOP. Aplican tarifas estándar de mensajes.',
  },
  'form.privacy': { en: 'Privacy Policy', es: 'Política de Privacidad' },
  'form.terms': { en: 'Terms', es: 'Términos' },
  'form.submit': { en: 'SHOW ME MY OPTIONS', es: 'VER MIS OPCIONES' },
  'form.sending': { en: 'SENDING…', es: 'ENVIANDO…' },
  'form.note': {
    en: 'Your information is private. No obligation.',
    es: 'Tu información es privada. Sin compromiso.',
  },
  'form.err.firstName': { en: 'Please enter your first name.', es: 'Por favor escribe tu nombre.' },
  'form.err.phone': {
    en: 'Please enter a valid phone number.',
    es: 'Por favor escribe un teléfono válido.',
  },
  'form.err.email': { en: 'Please enter a valid email.', es: 'Por favor escribe un email válido.' },
  'form.err.consent': {
    en: 'Please accept the consent to continue.',
    es: 'Por favor acepta el consentimiento para continuar.',
  },
  'form.err.submit': {
    en: 'We couldn’t send your information. Please try again or message a Queen directly.',
    es: 'No pudimos enviar tu información. Intenta de nuevo o escríbele directo a una Queen.',
  },
  'form.retry': { en: 'TRY AGAIN', es: 'INTENTAR DE NUEVO' },

  // Success
  'success.title': { en: 'WE GOT YOU, {name}.', es: 'LISTO, {name}.' },
  'success.sub': {
    en: 'Your information was received. Continue directly with Gabriela or Chanira on WhatsApp.',
    es: 'Recibimos tu información. Continúa directamente con Gabriela o Chanira por WhatsApp.',
  },

  // How it works
  'how.title': { en: 'HOW IT WORKS', es: 'CÓMO FUNCIONA' },
  'how.1.title': { en: 'TELL US WHAT YOU NEED', es: 'CUÉNTANOS QUÉ NECESITAS' },
  'how.2.title': { en: 'EXPLORE YOUR OPTIONS', es: 'EXPLORA TUS OPCIONES' },
  'how.3.title': { en: 'PICK UP YOUR KEYS', es: 'RECIBE TUS LLAVES' },

  // FAQ
  'faq.title': { en: 'FREQUENT QUESTIONS', es: 'PREGUNTAS FRECUENTES' },
  'faq.q1': { en: 'Do I need perfect credit?', es: '¿Necesito crédito perfecto?' },
  'faq.q2': {
    en: 'Can I buy while rebuilding my credit?',
    es: '¿Puedo comprar mientras reconstruyo mi crédito?',
  },
  'faq.q3': { en: 'New and used?', es: '¿Nuevos y usados?' },
  'faq.q4': { en: 'Can I lease?', es: '¿Puedo hacer leasing?' },
  'faq.q5': {
    en: 'Can I trade in my current vehicle?',
    es: '¿Puedo entregar mi carro actual como trade-in?',
  },
  'faq.q6': { en: 'Can I start on WhatsApp?', es: '¿Puedo empezar por WhatsApp?' },
  'faq.q7': { en: 'Do you speak Spanish?', es: '¿Hablan español?' },
  'faq.q8': { en: 'Where are you located?', es: '¿Dónde están ubicadas?' },
  'faq.generic': {
    en: 'Options vary based on the vehicle, your situation, eligibility, and the programs available at the time. Send us your information and we’ll walk you through what applies to you.',
    es: 'Las opciones varían según el vehículo, tu situación, la elegibilidad y los programas disponibles en ese momento. Envíanos tu información y te explicamos qué aplica en tu caso.',
  },
  'faq.a6': {
    en: 'Yes. You can start the conversation on WhatsApp with the Queen you choose, and continue at your own pace.',
    es: 'Sí. Puedes empezar la conversación por WhatsApp con la Queen que elijas y continuar a tu ritmo.',
  },
  'faq.a7': {
    en: 'Yes — Gabriela and Chanira assist you in Spanish and English.',
    es: 'Sí — Gabriela y Chanira te atienden en español e inglés.',
  },
  'faq.a8.base': {
    en: 'Gabriela and Chanira are automotive sales professionals at Doral Kia, in Doral, Florida.',
    es: 'Gabriela y Chanira son profesionales de ventas automotrices en Doral Kia, en Doral, Florida.',
  },
  'faq.q9': {
    en: 'Do you take my current car as part of the payment?',
    es: '¿Aceptan mi carro actual como parte de pago?',
  },
  'faq.a9': {
    en: 'Yes, trade-ins are evaluated. What your vehicle is worth depends on the vehicle itself and its condition, so we review it with you before anything is decided.',
    es: 'Sí, los trade-ins se evalúan. Lo que vale tu vehículo depende del vehículo y de su condición, así que lo revisamos contigo antes de decidir nada.',
  },
  'faq.q10': {
    en: 'Do you have certified pre-owned vehicles?',
    es: '¿Tienen vehículos usados certificados?',
  },
  'faq.a10': {
    en: 'Yes, Kia Certified Pre-Owned is available. The specifics vary by inventory at the time.',
    es: 'Sí, hay Kia Certified Pre-Owned disponible. Los detalles varían según el inventario del momento.',
  },
  'faq.q11': { en: 'Are there special programs?', es: '¿Hay programas especiales?' },
  'faq.a11': {
    en: 'Manufacturer incentive programs exist, including offers for active military. Eligibility and availability change, so we confirm what applies to you.',
    es: 'Existen programas de incentivos del fabricante, incluyendo ofertas para militares activos. La elegibilidad y disponibilidad cambian, así que confirmamos qué aplica en tu caso.',
  },

  // Final CTA
  'final.title': { en: 'READY FOR YOUR NEXT CAR?', es: '¿LISTO PARA TU PRÓXIMO CARRO?' },
  'final.photo': {
    en: '[REPLACE WITH FINAL GABRIELA + CHANIRA PHOTO]',
    es: '[REEMPLAZAR CON FOTO FINAL GABRIELA + CHANIRA]',
  },

  // Queen picker
  'picker.title': { en: 'Who would you like to talk to?', es: '¿Con quién quieres hablar?' },
  'picker.sub': {
    en: 'Pick a Queen and continue the conversation on WhatsApp.',
    es: 'Elige una Queen y continúa la conversación por WhatsApp.',
  },
  'picker.button': { en: 'WhatsApp', es: 'WhatsApp' },
  'picker.close': { en: 'Close', es: 'Cerrar' },
  'picker.unavailable': {
    en: 'WhatsApp not configured yet',
    es: 'WhatsApp aún no configurado',
  },
  'picker.msg.named': {
    en: 'Hi Queens, I’m {name}. I just submitted my information on miamicarqueen.com. I’m looking for {vehicle} and would like to learn about my options.',
    es: 'Hola Queens, soy {name}. Acabo de enviar mi información en miamicarqueen.com. Estoy buscando {vehicle} y quisiera conocer mis opciones.',
  },
  'picker.msg.generic': {
    en: 'Hi Queens, I found you on miamicarqueen.com and would like to learn about my options for my next vehicle.',
    es: 'Hola Queens, las encontré en miamicarqueen.com y quisiera conocer mis opciones para mi próximo carro.',
  },

  // Sticky CTA
  'sticky.cta': { en: 'TALK TO THE QUEENS', es: 'HABLAR CON LAS QUEENS' },

  // Footer
  'footer.services': {
    en: 'NEW · USED · LEASING · FINANCING',
    es: 'NUEVOS · USADOS · LEASING · FINANCIAMIENTO',
  },
  'footer.location': { en: 'Miami / Doral, Florida', es: 'Miami / Doral, Florida' },
  'footer.people': { en: 'Gabriela + Chanira', es: 'Gabriela + Chanira' },
  'footer.instagram': { en: 'Instagram', es: 'Instagram' },
  'footer.dealerLabel': {
    en: 'Gabriela and Chanira sell at',
    es: 'Gabriela y Chanira venden en',
  },
  'footer.notDealer': {
    en: 'Miami Car Queens is the personal brand of two automotive sales professionals. It is not a dealership.',
    es: 'Miami Car Queens es la marca personal de dos profesionales de ventas automotrices. No es un concesionario.',
  },
}

export function translate(key: string, lang: Language): string {
  const entry = translations[key]
  if (!entry) return key
  return entry[lang]
}
